import { isRouteAvailableInLocale } from "@/lib/i18n/route-equivalence";
import { getSiteUiCopy } from "@/lib/site-ui-copy";
import navmenu from "@/data/navmenu.json";
import { DEFAULT_LANG } from "@/lib/constants";
import { isResourceHubPublished } from "@/lib/cc-resources-nav-meta";
import {
  buildBlogHref,
  buildLocalizedPath,
  type NavCustomPath,
} from "@/lib/i18n/paths";
import { toSiteLocale, type SiteLocale } from "@/lib/i18n/config";
import type { Lang } from "@/lib/types";

export type NavResourceKey =
  | "blog"
  | "web-agency"
  | "ai-visibility"
  | "compare"
  | "industries"
  | "privacy-policy";

export type NavResourceItem = {
  key: NavResourceKey;
  label: string;
  href: string;
};

/** Top-level resource hubs in footer + resources trigger (excludes retired IA). */
const RESOURCE_ORDER: NavResourceKey[] = [
  "blog",
  "compare",
  "industries",
  "privacy-policy",
];

const RESOURCE_NAV_CUSTOM_PATH: Record<
  Exclude<NavResourceKey, "blog">,
  NavCustomPath
> = {
  "web-agency": "web-agency",
  "ai-visibility": "ai-visibility",
  compare: "compare",
  industries: "industries",
  "privacy-policy": "privacy-policy",
};

function findNavmenuTitle(customPath: string, lang: Lang): string | undefined {
  const locale = toSiteLocale(lang);
  const item = navmenu.main.find((m) => m.en?.customPath === customPath);
  return item?.[locale]?.title;
}

export function getResourcesNavLabel(lang: Lang = DEFAULT_LANG): string {
  return findNavmenuTitle("resources", lang) ?? getSiteUiCopy(lang).navigation.resources;
}

export function getResourcesNavHref(lang: Lang = DEFAULT_LANG): string {
  const locale = toSiteLocale(lang);
  return buildLocalizedPath({ locale, customPath: "resources" });
}

export function buildResourceItemHref(key: NavResourceKey, locale: SiteLocale): string {
  if (key === "blog") {
    return buildBlogHref(locale);
  }
  return buildLocalizedPath({
    locale,
    customPath: RESOURCE_NAV_CUSTOM_PATH[key],
  });
}

function resourceLabel(key: NavResourceKey, lang: Lang): string {
  const fromNav =
    key === "blog"
      ? findNavmenuTitle("blog", lang)
      : findNavmenuTitle(RESOURCE_NAV_CUSTOM_PATH[key], lang);

  if (fromNav) {
    return fromNav;
  }

  return getSiteUiCopy(lang).resourceFallbacks[key];
}

export function getSiteNavResourceItems(lang: Lang = DEFAULT_LANG): NavResourceItem[] {
  const locale = toSiteLocale(lang);
  return RESOURCE_ORDER.filter((key) => isResourceHubPublished(key) && isRouteAvailableInLocale({kind: key}, locale)).map((key) => ({
    key,
    label: resourceLabel(key, lang),
    href: buildResourceItemHref(key, locale),
  }));
}

export function isResourcesNavTriggerHref(
  href: string,
  locale: SiteLocale,
): boolean {
  return href === buildLocalizedPath({ locale, customPath: "resources" });
}
